// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import Progress from 'presentations/views/components/atoms/Progress';
import Popover from 'presentations/views/components/atoms/Popover';
import styled from '@emotion/styled';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Button from 'presentations/views/components/atoms/Button';
import {
  black,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import {
  Currency,
  getPlanName,
  getPriceText,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import { SyntheticEvent, useMemo } from 'react';
import { usePricingTable } from './hooks';
import Subscription from 'domains/models/subscription';
import { getDateTime } from 'infras/datetime';
import {
  latestPlanVersion,
  PlanVersion,
} from 'domains/models/subscription/planVersion';
import { TrialChannel } from 'domains/models/subscription/trial_channel';

export interface Props {
  onClickPrice?: any;
  planId: PlanId | null;
  filterPlanId?: PlanId;
  planVersion: PlanVersion | null;
  currency: Currency;
  intervalType: IntervalType;
  isLoading?: boolean;
  isLogin: boolean;
  canChange: boolean;
  trialStartedAt: string | null | undefined;
  trialEndedAt: string | null | undefined;
  trialChannel: TrialChannel | null | undefined;
  currentIntervalType: IntervalType | null;
  isChangeAnnualToMonth: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = usePricingTable(props);
  const { plans, filterPlanIndex } = useMemo(() => {
    let plans = state.plans;
    let filterPlanIndex = null;
    if (props.filterPlanId) {
      plans = state.plans.filter((planId) => planId === props.filterPlanId);
      filterPlanIndex = state.plans.indexOf(props.filterPlanId);
    }
    return { plans, filterPlanIndex };
  }, [state.plans, props.filterPlanId]);
  const isCurrentEp = Boolean(
    props.planId && props.planId >= PlanId.Enterprice,
  );

  return (
    <>
      {state.sections.map((section, sectionIndex) => (
        <>
          <PricingTitleTableRow>
            <PricingTableRowHeader>
              <PricingTableTitleContainer>
                {state.locale !== 'en' && (
                  <PricingTableSubTitle>{section.title}</PricingTableSubTitle>
                )}
                <PricingTableTitle>{t(section.title)}</PricingTableTitle>
              </PricingTableTitleContainer>
            </PricingTableRowHeader>
            {plans.map((planId: PlanId) => {
              const isEp = planId === PlanId.Enterprice;
              const isJicooTrialing = Subscription.isJicooTrialing(
                props.trialChannel,
                getDateTime(props.trialEndedAt),
              );
              const isSelected = isJicooTrialing
                ? planId === PlanId.Free
                : props.planId === planId &&
                  (planId === PlanId.Free ||
                    (props.planVersion === latestPlanVersion &&
                      props.currentIntervalType === props.intervalType));
              let priceName = t('SignUp');
              if (props.isLogin) {
                if (isSelected) {
                  priceName = t('Selected');
                } else {
                  if (props.planId && props.planId < planId) {
                    priceName = props.trialStartedAt
                      ? t('Upgrade')
                      : t('Trial');
                  } else {
                    priceName = t('Select');
                  }
                }
              } else {
                if (planId >= PlanId.Pro) {
                  priceName = t('Trial');
                }
              }
              if (planId === PlanId.Enterprice) {
                priceName = t('Contact');
              }
              const isDisabled = props.isLogin && props.canChange === false;
              return (
                <PricingTableRowHeaderCell key={planId}>
                  {sectionIndex === 0 && (
                    <TablePlan isEp={isEp}>
                      <PlanTitle>
                        {getPlanName(planId, latestPlanVersion)}
                      </PlanTitle>
                      <PlanPrice>
                        {getPriceText(
                          planId,
                          props.currency,
                          props.intervalType,
                          latestPlanVersion,
                        )}
                      </PlanPrice>
                      <PlanPriceMeta>
                        {!isEp && (
                          <PlanPriceUnit>{t(`Price period`)}</PlanPriceUnit>
                        )}
                      </PlanPriceMeta>
                      <PlanButton
                        variant={isEp ? 'outlined' : 'contained'}
                        isDisabled={
                          (!isEp && isDisabled) ||
                          props.isLoading ||
                          isSelected ||
                          props.isChangeAnnualToMonth ||
                          isCurrentEp
                        }
                        color="primary"
                        isEp={isEp}
                        onClick={(e: SyntheticEvent) =>
                          props.onClickPrice(planId)
                        }
                      >
                        {props.isLoading ? (
                          <Progress color="inherit" size={18} />
                        ) : (
                          priceName
                        )}
                      </PlanButton>
                    </TablePlan>
                  )}
                </PricingTableRowHeaderCell>
              );
            })}
          </PricingTitleTableRow>
          <PricingTable>
            {section.functions.map((cell, index) => (
              <PricingTableRow key={index}>
                <PricingTableCellHeader>
                  {cell.title}
                  {cell.info && (
                    <DescriptionIconButton
                      onClick={(e: any) =>
                        actions.onTooltipOpen(e, cell.info, cell.url)
                      }
                    >
                      <DescriptionIcon name="info" />
                    </DescriptionIconButton>
                  )}
                </PricingTableCellHeader>
                {cell.checks.map((check, index) => {
                  if (filterPlanIndex !== null && index !== filterPlanIndex) {
                    return null;
                  }
                  return (
                    <PricingTableCell key={index}>
                      {typeof check === 'boolean' ? (
                        check === true && <CheckIcon name={'check-circle'} />
                      ) : (
                        <CellText>{check}</CellText>
                      )}
                    </PricingTableCell>
                  );
                })}
              </PricingTableRow>
            ))}
          </PricingTable>
        </>
      ))}
      {state.anchor && (
        <Popover
          maxWidth={300}
          anchor={state.anchor}
          onClose={actions.onTooltipClose}
        >
          <Description
            dangerouslySetInnerHTML={{ __html: state.description || '' }}
          />
        </Popover>
      )}
    </>
  );
}

const PricingTable = styled.div`
  margin-bottom: ${spacing(64)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  background-color: ${white};
  color: ${black};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
const PricingTableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${gray04};
  :last-of-type {
    border-bottom: 0;
  }
`;
const PricingTitleTableRow = styled(PricingTableRow)`
  margin-bottom: ${spacing(16)};
  border-bottom: 0;
`;
const PricingTableRowHeader = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1;
`;
const PricingTableRowHeaderCell = styled.div`
  width: 130px;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const TablePlan = styled.div<{ isEp: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing(12)};
  background-color: ${(props) => (props.isEp ? primary : white)};
  color: ${(props) => props.isEp && white};
  width: 122px;
  border-radius: ${borderRadius(16)};
  height: 200px;
  position: relative;
`;
const PricingTableCellHeader = styled.div`
  flex: 1;
  padding: 0 ${spacing(16)};
  display: flex;
  align-items: center;
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    width: 180px;
    min-width: auto;
  }
  ${mediaMax.smartphone} {
    width: 80px;
  }
  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
    padding: 0 ${spacing(8)};
  }
`;
const PricingTableCell = styled.div`
  text-align: center;
  padding: ${spacing(16)} 0;
  width: 130px;
  border-left: 1px solid ${gray04};
  ${mediaMax.tablet} {
    width: 80px;
  }
`;
const CheckIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;

const PlanTitle = styled.div`
  margin: ${spacing(16)} 0 ${spacing(8)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(18)};
`;
const PlanPrice = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(28)};
  line-height: 1;
  height: 40px;
`;
const PlanPriceMeta = styled.div`
  display: flex;
  font-size: ${fontSize(10)};
  margin-bottom: ${spacing(16)};
  justify-content: center;
`;
const PlanPriceUnit = styled.div`
  color: gray02;
`;
const PlanButton = styled(Button)<{ isEp: boolean }>`
  margin-bottom: ${spacing(16)};
  width: 100%;
  font-size: ${fontSize(12)};
  word-break: keep-all;
  background-color: ${(props) => props.isEp && primary};
  color: ${(props) => props.isEp && whiteFix};
  border-width: ${(props) => props.isEp && 1};
  border-color: ${(props) => props.isEp && whiteFix};

  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  width: calc(100% - 24px);
  margin-left: 12px;
`;
const DescriptionIconButton = styled(IconButton)`
  padding: ${spacing(8)};
  ${mediaMax.smartphone} {
    display: none;
  }
`;
const DescriptionIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  margin: 0;
`;
const Description = styled.div`
  padding: ${spacing(8)} ${spacing(16)};
  font-size: ${fontSize(12)};
`;
const CellText = styled.span`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(11)};
  }
`;
const PricingTableTitleContainer = styled.div`
  /* margin-bottom: ${spacing(24)}; */
`;

const PricingTableTitle = styled.h2`
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(16)};
  }
`;
const PricingTableSubTitle = styled.div`
  font-size: ${fontSize(12)};
  color: ${primary};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(4)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;
